import { TZDate } from '@date-fns/tz';
import clsx from 'clsx';
import { useRef } from 'react';

import {
  calendarClearBgCSS,
  calendarFieldCSS,
  calendarRangeFieldCSS,
} from './Calendar.sty.js';
import { CalendarItem } from './CalendarItem.js';
import { CalendarOutlineIcon } from './CalendarOutlineIcon.js';
import { useDay } from './hooks/useDay.js';
import { NUMBER_OF_WEEKDAYS } from './types/calendarConfiguration.js';
import { useFormFieldContext } from '../forms-core/hooks/useFormFieldContext.js';
import { useFormFieldMessagesContext } from '../forms-core/hooks/useFormFieldMessagesContext.js';

/** @internal */
export interface DayProps {
  /** The day it represents. */
  date: TZDate;
  /** Whether the day is part of the current month */
  isCurrentMonthDay?: boolean;
  /** Position in the calendar grid 1 is first day and 7 the last */
  position: number;
}

/**
 * Day component
 * Renders one single day of a calendar month
 */
export const Day = ({ date, isCurrentMonthDay = true, position }: DayProps) => {
  // Reference of the day button, needed to pass to the hook for proper focus management
  const itemRef = useRef<HTMLDivElement>(null);
  const isLastInRow = position % NUMBER_OF_WEEKDAYS === 0;
  const isFirstInRow = position % NUMBER_OF_WEEKDAYS === 1;

  const {
    ariaLabel,
    tabIndex,
    isCurrent,
    onClick,
    isDisabled,
    rangeInteractionProps,
    highlightColor,
    highlightType,
    isSelected,
    isIncompleteSelection,
    selectionVariant,
  } = useDay({
    date,
    itemRef,
  });
  const { disabled, ariaDisabled } = useFormFieldContext();
  const { state } = useFormFieldMessagesContext();

  const validatedHighlightColor =
    state.showError && highlightColor === 'primary'
      ? 'critical'
      : highlightColor;

  return (
    <td
      role="presentation"
      className={clsx(
        calendarFieldCSS({
          color: validatedHighlightColor,
          isDisabled: isDisabled || disabled || ariaDisabled,
          isIncompleteSelection: isIncompleteSelection,
        }),
        highlightType && highlightType !== 'same'
          ? calendarRangeFieldCSS({
              rangeKind: highlightType,
            })
          : '',
        isSelected ? calendarClearBgCSS : '',
      )}
      {...rangeInteractionProps}
    >
      <CalendarItem
        ref={itemRef}
        className="calendar-button calendar-body-button"
        isDisabled={isDisabled || disabled}
        isCurrent={isCurrent}
        isSelected={isSelected}
        highlightColor={validatedHighlightColor}
        isCurrentMonthDay={isCurrentMonthDay}
        tabIndex={tabIndex}
        ariaLabel={ariaLabel}
        role="gridcell"
        aria-selected={isSelected}
        onClick={onClick}
        view="day"
      >
        {date.getDate()}
        <CalendarOutlineIcon
          isCurrent={isCurrent}
          variant={selectionVariant}
          highlightType={
            (isFirstInRow && highlightType === 'last') ||
            (isLastInRow && highlightType === 'first')
              ? undefined
              : highlightType
          }
        />
      </CalendarItem>
    </td>
  );
};
Day['displayName'] = 'Day';
